/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Render the per-student record inside a class.
 * @state-owner dataset store
 * @allowed-imports React; dataset and workspace stores; shared components, selectors and hooks
 * @forbidden direct API client; student-only surfaces
 * @public-api workspace main column
 * @side-effects navigation to reports and live intervention
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { BackButton, WrapPage } from '../../../shared/components/PageShell';
import { RUN_MODE_LABEL, RUN_STATUS_LABEL } from '../../../shared/lib/demo-data';
import {
  classRuns,
  findSeat,
  runName,
  seatPresentation,
  seatStatusLabel,
} from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';

export function StudentPage({ classIndex, student }: { classIndex: number; student: string }) {
  const t = useTranslator();
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const runs = useDatasetStore((state) => state.runs);
  const seats = useDatasetStore((state) => state.seats);
  const closeItem = useWorkspaceUiStore((state) => state.closeItem);
  const navigate = useWorkspaceUiStore((state) => state.navigate);
  const openItem = useWorkspaceUiStore((state) => state.openItem);

  const rows = classRuns(runs, classIndex);
  const seat = findSeat(seats[classIndex] ?? [], student);
  const live = rows.some((run) => run.status === 'live');
  const others = classes
    .map((_, index) => index)
    .filter((index) => index !== classIndex && classes[index].students.includes(student));

  const openIntervention = () => {
    const run = rows.find((item) => item.status === 'live');
    if (!run) return;
    navigate('live', run.id, { open: { k: 'iv', n: student } });
  };

  const openRun = (runId: string) => {
    const run = runs.find((item) => item.id === runId);
    if (!run) return;
    if (run.status === 'live') {
      navigate('live', runId);
      return;
    }
    openItem({ k: run.status === 'open' ? 'prog' : 'rev', id: runId });
  };

  return (
    <WrapPage maxWidth={900}>
      <div className="hd">
        <BackButton onSelect={closeItem} title={`${t('返回')} ${classes[classIndex].name}`} />
        <div>
          <h1>{student}</h1>
          <p className="sub">
            {`${live && seat ? seatStatusLabel(seat, t) : t('目前沒有進行中的活動')}${
              others.length
                ? ` · ${t('也在')} ${others.map((index) => classes[index].name).join('、')}`
                : ''
            }`}
          </p>
        </div>
        <div className="sp" />
        <button className="bt" type="button" onClick={() => navigate('rep', student)}>
          {t('檢視學習報告')}
        </button>
        {live && seat && seatPresentation(seat.status).needsTeacher ? (
          <button className="bt pri" type="button" onClick={openIntervention}>
            {t('開啟接手包')}
          </button>
        ) : null}
      </div>
      <section className="panel flush">
        <header>
          <h2>{t('在這個班的紀錄')}</h2>
          <span className="n">{`${rows.length} 份`}</span>
        </header>
        <div>
          {rows.map((run) => (
            <button className="it" key={run.id} type="button" onClick={() => openRun(run.id)}>
              <div className="av">{t('題')}</div>
              <div className="t">
                <b>{runName(activities, run)}</b>
                <span>
                  {`${t(run.dateLabel)} · ${t(RUN_MODE_LABEL[run.mode])} · ${t(RUN_STATUS_LABEL[run.status])}`}
                </span>
              </div>
              <em>{run.status === 'live' ? (seat ? seatStatusLabel(seat, t) : '') : '88 分'}</em>
            </button>
          ))}
        </div>
      </section>
    </WrapPage>
  );
}
